import { useEffect, useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { Badge, Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { EmptyState, Timeline } from '@vnetwork/ui/data';
import { useCustomerActivityLogs, type ActivityLogRow } from '@/api/customer-activity';
import { activityLogToTimelineItem } from '../activity-log-item';
import { TabError, TabLoading } from '../tab-states';
import { actionOptionLabel, distinctActionTypes, groupByDate } from './activity-log-groups';
import { DateRangeInput } from '@/components/date-input';

/**
 * Full Activity Logs tab for the customer detail — the dedicated, full-width
 * version of the mini Basic-Information timeline. Fetches larger pages (50) via
 * `useCustomerActivityLogs` with a "Load more" pager, groups rows by local date,
 * and reuses `ActivityLogItem` for the exact same row visual (dot/line, actor,
 * action chip, {old,new} field-diff). A client-side action-type filter is
 * derived from the loaded rows — no hardcoded enum guessing.
 *
 * Date-range (From / To) inputs send `gte`/`lte` to the server, resetting the
 * accumulated list and returning to page 1 on change.
 */

const PAGE_SIZE = 50;
const ALL = '__all__';

export interface ActivityLogsTabProps {
  customerId: number;
}

export default function ActivityLogsTab({ customerId }: ActivityLogsTabProps) {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<ActivityLogRow[]>([]);
  const [action, setAction] = useState<string>(ALL);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const query = useCustomerActivityLogs(customerId, {
    page,
    page_size: PAGE_SIZE,
    gte: dateFrom || undefined,
    lte: dateTo ? `${dateTo}T23:59:59` : undefined,
  });
  const pageRows = query.data?.results;
  const pagination = query.data?.pagination;

  // Reset accumulator + page when date range changes (server-side filter switch).
  useEffect(() => {
    setAccumulated([]);
    setPage(1);
  }, [dateFrom, dateTo]);

  // Append each fetched page into the running list, de-duped by log_id. Keyed on
  // the query's own data so it commits exactly once per successful fetch.
  useEffect(() => {
    if (!pageRows) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((r) => r.log_id));
      const next = pageRows.filter((r) => !seen.has(r.log_id));
      return next.length ? [...prev, ...next] : prev;
    });
  }, [pageRows]);

  const actionOptions = useMemo(() => distinctActionTypes(accumulated), [accumulated]);
  const filtered = useMemo(
    () => (action === ALL ? accumulated : accumulated.filter((r) => r.action_type === action)),
    [accumulated, action],
  );
  const groups = useMemo(() => groupByDate(filtered), [filtered]);

  const totalPages = pagination?.total_pages ?? 1;
  const hasMore = page < totalPages;
  const total = pagination?.total ?? accumulated.length;

  if (query.isError && accumulated.length === 0) {
    return <TabError title="Failed to load activity logs" onRetry={() => query.refetch()} />;
  }
  if (query.isLoading && accumulated.length === 0) {
    return <TabLoading rows={8} />;
  }

  return (
    <Card flat>
      <CardBody>
        <Toolbar
          start={
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 'var(--vn-space-2)' }}>
              <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>Activity history</span>
              {total ? (
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  {total} total
                </span>
              ) : null}
            </span>
          }
          end={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              {/* Server-side date range filter */}
              <DateRangeInput
                aria-label="Date range"
                from={dateFrom}
                to={dateTo}
                onChange={(from, to) => {
                  setDateFrom(from);
                  setDateTo(to);
                }}
                style={{ width: 240 }}
              />

              {/* Client-side action-type filter */}
              {actionOptions.length > 1 ? (
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger size="sm" style={{ minWidth: 180 }} aria-label="Filter by action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All actions</SelectItem>
                    {actionOptions.map((a) => (
                      <SelectItem key={a} value={a}>
                        {actionOptionLabel(a)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          }
        />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<History size={28} />}
            title="No activity"
            description={
              action === ALL
                ? 'Actions on this customer will appear here.'
                : 'No activity matches the selected action.'
            }
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--vn-space-5)',
              marginTop: 'var(--vn-space-4)',
            }}
          >
            {groups.map((group) => (
              <section key={group.key}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    marginBottom: 'var(--vn-space-3)',
                  }}
                >
                  <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
                    {group.label}
                  </span>
                  <Badge tone="neutral" appearance="subtle" size="sm">
                    {group.rows.length}
                  </Badge>
                </div>
                <Timeline items={group.rows.map(activityLogToTimelineItem)} size="sm" />
              </section>
            ))}

            {hasMore && action === ALL && (
              <div style={{ textAlign: 'center' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={query.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
